import { Alert, Button, CircularProgress, Stack } from '@mui/material';
import {
  useGetClientCuisineQuery,
  useGetClientCuisineListQuery,
} from 'api/client-domain/client-domain';
import { useGetBenchmarkTimeframesQuery } from 'api/reports-service/guest-journey-reports';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { BenchmarksBootstrapData } from '../guest-journey.hooks';

export type BenchmarksBootstrapProps = {
  /** Rendered only once all three bootstrap values have resolved. */
  children: (data: BenchmarksBootstrapData) => ReactNode;
};

/**
 * Blocks the Benchmarks tab until the three BE values its filters + KPI require
 * are in hand: the client cuisine (`GET guest-journey/cuisine`), which backs
 * both the "Brand Cuisine" KPI and the comparison baseline that seeds the
 * *required* `benchmarkCuisine` filter default; the cuisine filter options
 * (`GET clients/cuisine/list`); and the timeframe filter options
 * (`GET benchmarks/timeframes`). Fetching them up front lets the Timeframe and
 * Cuisine filters use valid, synchronous defaults instead of racy `asyncOptions`
 * on required query params. Shows a loader while fetching and a retryable error
 * alert on failure; `children` runs only when all three have data.
 */
const BenchmarksBootstrap = ({ children }: BenchmarksBootstrapProps) => {
  const { t } = useTranslation();

  const clientCuisine = useGetClientCuisineQuery();
  const cuisines = useGetClientCuisineListQuery();
  const timeframes = useGetBenchmarkTimeframesQuery();

  if (clientCuisine.data && cuisines.data && timeframes.data) {
    return (
      <>
        {children({
          // KPI shows the human-readable title; the filter default/baseline is
          // the slug the benchmark taxonomy is keyed by (`benchmarkCuisine`).
          brandCuisine: clientCuisine.data.displayTitle,
          cuisineDefault: clientCuisine.data.cuisine,
          cuisineOptions: cuisines.data,
          yearOptions: timeframes.data,
        })}
      </>
    );
  }

  if (
    clientCuisine.isFetching ||
    cuisines.isFetching ||
    timeframes.isFetching
  ) {
    return (
      <Stack
        alignItems="center"
        paddingY={8}
        data-testid="Benchmarks.BootstrapLoading"
      >
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Alert
      severity="error"
      data-testid="Benchmarks.BootstrapError"
      action={
        <Button
          color="inherit"
          size="small"
          onClick={() => {
            clientCuisine.refetch();
            cuisines.refetch();
            timeframes.refetch();
          }}
          data-testid="Benchmarks.BootstrapError.Retry"
        >
          {t('page.guestJourney.benchmarks.bootstrapError.retry')}
        </Button>
      }
    >
      {t('page.guestJourney.benchmarks.bootstrapError.message')}
    </Alert>
  );
};

export default BenchmarksBootstrap;
